<template>
  <div>
    <AutoCompleteShell
      ref="autocomplete"
      :label="label"
      :name="name"
      :hint="hint"
      :placeholder="placeholderText"
      :disabled="isDisabled"
      :error-messages="allErrors"
      @update:search="updateSearch"
      @clear="clear"
      @open="restoreToPreviousState"
      @close="onClose"
      @close-expand="onCloseExpand"
    >
      <template #popover>
        <chip-filter
          class="ma-2 pt-2"
          :items="locationTypes"
          :value="locationType"
          @update="selectLocationType"
        />
        <div v-if="parentName" class="header">
          <vs-button
            class="parent-location-button"
            small
            type="secondary"
            icon="mdi-chevron-left"
            :label="parentName"
            @click.stop="navigateBack"
          />
        </div>
        <picker-list-virtualized
          ref="virtualizedList"
          :items="items"
          @near-bottom="continueFetching"
        >
          <template #item="{ item }">
            <picker-list-item
              :item="item"
              :is-selected="isSelected(item)"
              :truncate="false"
              @expand="navigateIntoItem"
              @select="selectItem"
            />
          </template>
          <template #after>
            <v-container v-if="isFetching" class="my-2">
              <v-row justify="center">
                <vs-text center>loading...</vs-text>
              </v-row>
            </v-container>
          </template>
        </picker-list-virtualized>
        <div v-if="noResultsMessage" class="no-data">
          <v-container class="my-2">
            <v-row justify="center">
              <vs-text center>{{ noResultsMessage }}</vs-text>
            </v-row>
          </v-container>
        </div>
        <vs-button
          v-if="showAddLocation && (!canShowAllLocations || showAllLocations)"
          full-width
          type="secondary"
          icon="mdi-plus"
          label="Add new location"
          class="force-slight-overflow"
          rectangular
          remove-invisible-border
          @click.stop="onAddNewLocationClick"
        />
        <vs-button
          v-if="canShowAllLocations && !showAllLocations"
          full-width
          type="secondary"
          icon="mdi-login"
          label="Show all locations"
          class="force-slight-overflow"
          rectangular
          remove-invisible-border
          @click.stop="onShowMoreResultsClick"
        />
      </template>
    </AutoCompleteShell>
    <portal v-if="isNewLocationFormVisible" to="job-on-the-fly-location-form-bottom-sheet">
      <on-the-fly-location-form
        data-test="new-location-form"
        :default-parent-loc-id="parentId"
        @close="closeNewLocationForm"
      />
    </portal>
  </div>
</template>

<script setup lang="ts">
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import AutoCompleteShell from '@/components/vision/next/inputs/AutoCompleteShell.vue'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import PickerListItem from '@/components/vision/next/picker-list/PickerListItem.vue'
import ChipFilter from '@/components/vision/next/ChipFilter.vue'
import OnTheFlyLocationForm from '@/components/locations/on-the-fly-locations/OnTheFlyLocationForm.vue'
import { useHeadlessLocationPicker } from './HeadlessLocationPicker'
import { computed, nextTick, ref, toRef } from 'vue'
import { HeadlessAutoCompleteValueType } from '../vision/next/inputs/useAutoComplete'
import { useFormField } from '@/components/vision/next/forms/useFormAdapter'
import { fetchLocationById } from '@/api/locations'
import { useQueryClient } from '@tanstack/vue-query'
import { useHubs } from '@/common/useHubs'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'

const props = defineProps<{
  label: string
  name?: string
  hint?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  errorMessages?: string[]
  multiple?: boolean
  value?: number | number[]
  initialLocationName?: string
  excludeLocationIds?: number[]
  showAddLocation?: boolean
  canShowAllLocations?: boolean
}>()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const emit = defineEmits<{
  (event: 'input', value: number | number[] | undefined)
}>()

const valueAsRef = toRef(props, 'value')

const autocomplete = ref<InstanceType<typeof AutoCompleteShell>>()
const virtualizedList = ref<InstanceType<typeof PickerListVirtualized>>()

const isNewLocationFormVisible = ref(false)

const { currentHub } = useHubs()

// resolved as a ref so the picker follows the hub it is rendered under
const resolvedHubId = computed(() => currentHub.value?.id)

const showAllLocations = ref<boolean>(false)

const { allErrorMessages: remoteErrors, runValidation } = useFormField({
  value: valueAsRef,
  required: props.required,
})

const allErrors = computed(() => [...remoteErrors.value, ...(props.errorMessages ?? [])])

const isLoadingLocation = ref(false)
const isDisabled = computed(() => props.disabled || isLoadingLocation.value)
const placeholderText = computed(() => (isLoadingLocation.value ? 'Loading...' : props.placeholder))

const getScrollPosition = () => {
  return virtualizedList?.value?.getScrollPosition()
}

const scrollToPosition = (pos: number) => {
  return virtualizedList?.value?.scrollToPosition(pos)
}

const onNavigate = () => {
  autocomplete.value?.input?.focus()
}

const onValueChanged = (
  incoming?: HeadlessAutoCompleteValueType | HeadlessAutoCompleteValueType[],
) => {
  if (props.multiple) {
    const items = incoming as HeadlessAutoCompleteValueType[]
    emit(
      'input',
      items.map((i) => i.id as number),
    )
  } else {
    const item = incoming as HeadlessAutoCompleteValueType | undefined
    emit('input', item?.id)
    if (item) {
      autocomplete.value?.close()
    }
  }
}

const isSelected = (item: HeadlessAutoCompleteValueType) => {
  if (props.multiple) {
    return (valueAsRef.value as number[] | undefined)?.includes(item.id as number)
  }

  return item.id === valueAsRef.value
}

const client = useQueryClient()

const valueResolver = async (id) => {
  // if we've been provided the name already for the initial location then we can just return it without waiting
  if (id === props.value && props.initialLocationName) {
    return { id, title: props.initialLocationName }
  }
  isLoadingLocation.value = true
  try {
    const location = await fetchLocationById(client, id)
    isLoadingLocation.value = false
    return { id: location.id, title: location.location }
  } catch (error) {
    isLoadingLocation.value = false
    clear()
    return { id, title: 'Unknown' }
  }
}

const onClose = () => {
  nextTick().then(() => runValidation())
}

const onCloseExpand = () => {
  showAllLocations.value = false
  updateSearch('')
}

const onAddNewLocationClick = () => {
  isNewLocationFormVisible.value = true
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isCreatingLocationOnTheFlyBottomSheetVisible: true,
  })
  autocomplete.value?.close()
}

const onShowMoreResultsClick = () => {
  showAllLocations.value = true
}

const closeNewLocationForm = (locId) => {
  isNewLocationFormVisible.value = false
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isCreatingLocationOnTheFlyBottomSheetVisible: false,
  })
  if (locId) {
    emit('input', locId)

    client.invalidateQueries({
      queryKey: ['locations', {}],
    })
  }
}

const {
  items,
  isFetching,
  locationType,
  locationTypes,
  selectLocationType,
  parentName,
  parentId,
  updateSearch,
  continueFetching,
  navigateIntoItem,
  navigateBack,
  selectItem,
  noResultsMessage,
  clear,
  restoreToPreviousState,
} = useHeadlessLocationPicker({
  getScrollPosition,
  scrollToPosition,
  onNavigate,
  onValueChanged,
  multiple: props.multiple ?? false,
  value: valueAsRef,
  valueResolver,
  hubId: resolvedHubId,
  excludeLocationIds: props.excludeLocationIds,
  canShowAllLocations: props.canShowAllLocations,
  showAllLocations,
})
</script>

<style scoped>
.no-data {
  flex-grow: 1;
  height: 100%;
  width: 100%;
}

.header {
  padding: var(--space-small);
}

.parent-location-button {
  max-width: 100%;
  justify-content: flex-start;
  overflow: hidden;
}
</style>
